'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import DrawerCloseIcon from 'hec/assets/icons/drawer-close-icon'

import {cva, type VariantProps} from 'class-variance-authority'
import {cn} from '@/lib/utils'
import {isClient} from '@/utils/common'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const modalOverlayVariants = cva('', {
    variants: {
        type: {
            default:
                'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            addToCart:
                'fixed h-full w-full inset-0 bottom-0 z-1400 bg-black/50  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
        }
    },
    defaultVariants: {
        type: 'default'
    }
})

type Type = 'default' | 'addToCart'

interface DialogOverlayProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
        VariantProps<typeof modalOverlayVariants> {
    type?: Type
    className?: string
    'data-testid': string | undefined
}

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    DialogOverlayProps
>(({type = 'default', className, 'data-testid': dataTestId, ...props}, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(modalOverlayVariants({type}), className)}
        data-testid={dataTestId || 'dialog-DialogOverlay'}
        {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
        VariantProps<typeof modalOverlayVariants> {
    type?: Type
    dialogContainer?: HTMLElement | null
    className?: string
    closeIcon?: React.ReactNode
    hideCloseBtn?: boolean
    'data-testid': string | undefined
}

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    DialogContentProps
>(
    (
        {
            type = 'default',
            className,
            children,
            dialogContainer = isClient ? window.document.body : null,
            closeIcon = <DrawerCloseIcon />,
            hideCloseBtn = false,
            'data-testid': dataTestId,
            ...props
        },
        ref
    ) => (
        <DialogPortal container={dialogContainer} data-testid={dataTestId || 'dialog-DialogPortal'}>
            <DialogOverlay type={type} data-testid={dataTestId || 'dialog-DialogOverlay'} />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
                    className
                )}
                data-testid={dataTestId || 'dialog-DialogContent'}
                {...props}
            >
                {children}
                {hideCloseBtn ? (
                    ''
                ) : (
                    <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                        {closeIcon}
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    )
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
        {...props}
    />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
        {...props}
    />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        data-testid={dataTestId || 'dialog-DialogTitle'}
        {...props}
    />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
        className?: string
        'data-testid'?: string | undefined
    }
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        data-testid={dataTestId || 'dialog-DialogDescription'}
        {...props}
    />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription
}
